import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  View,
  Image,
  StyleSheet,
  FlatList,
  Pressable,
  useWindowDimensions,
  Text,
  ActivityIndicator,
} from "react-native";
import { SessionContext } from "../../../contexts";
import { TGetPatientImageData, getPatientImages } from "../../../services/api";
import { TPatientInfo } from "../../../services/api-clinic";
import GalleryModal from "./GalleryModal";

const tokens = {
  color: {
    text: "#0F172A",
    muted: "#64748B",
    surface: "#F8FAFC",
    border: "#E2E8F0",
    focus: "#2563EB",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
  },
  radius: {
    sm: 8,
    md: 12,
  },
  shadow: {
    none: 0,
  },
  font: {
    base: "System",
  },
  fontSize: {
    sm: 14,
    md: 16,
  },
  lineHeight: {
    sm: 20,
    md: 24,
  },
  zIndex: {
    base: 1,
  },
  motion: {
    microMs: 100,
    standardMs: 200,
    complexMs: 300,
  },
  size: {
    image: 150,
    emptyMinHeight: 120,
  },
};

const SimpleGalleryView: React.FC<{
  currentPatientDetail: TPatientInfo;
  availableWidth?: number;
  excludedUploadTypes?: string[];
}> = ({ currentPatientDetail, availableWidth, excludedUploadTypes = [] }) => {
  const session = useContext(SessionContext)?.session;
  const [imageList, setImageList] = useState<TGetPatientImageData[]>([]);
  const [validUrls, setValidUrls] = useState<Record<string, string>>({});
  const checkedUrlsRef = useRef<Record<string, boolean>>({});
  const lastFetchKeyRef = useRef<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { width: screenWidth } = useWindowDimensions();

  // Use either passed availableWidth, measured containerWidth, or fallback to screen width
  const effectiveWidth = availableWidth || containerWidth || screenWidth;

  // Calculate number of columns and use as key for FlatList
  const numColumns = Math.max(1, Math.floor(effectiveWidth / 160));

  const excludedKey = useMemo(
    () => excludedUploadTypes.slice().sort().join("|"),
    [excludedUploadTypes],
  );
  const excludedSet = useMemo(
    () => new Set(excludedUploadTypes),
    [excludedKey],
  );

  // Memoized function to check if image is loadable with caching
  const isImageLoadable = useCallback(async (url: string): Promise<boolean> => {
    // Return cached result if we've already checked this URL
    if (checkedUrlsRef.current[url] !== undefined) {
      console.log(
        `Using cached result for ${url.substring(0, 50)}...: ${checkedUrlsRef.current[url]}`,
      );
      return checkedUrlsRef.current[url];
    }

    console.log(`Checking URL: ${url.substring(0, 50)}...`);
    try {
      // Use GET without range header to test exactly what Image component will do
      const res = await fetch(url, {
        method: "GET",
        signal: AbortSignal.timeout(5000),
      });

      const result = res.ok;
      console.log(
        `URL check result for ${url.substring(0, 50)}...: ${result} (status: ${res.status})`,
      );

      // Cache the result
      checkedUrlsRef.current[url] = result;
      return result;
    } catch (error) {
      console.error(`URL check failed for ${url.substring(0, 50)}...:`, error);
      // Cache failed attempts to prevent retrying
      checkedUrlsRef.current[url] = false;
      return false;
    }
  }, []);

  // Memoize the full image URL mapping to prevent recalculation
  const fullImageUrlMap = useMemo(() => {
    return imageList.reduce<Record<string, string>>((acc, item) => {
      if (excludedSet.has(item.image_data?.upload_type)) return acc;
      const id = String(item.image_data?.id ?? "");
      const fullUrl = item.presigned_urls?.[0];
      if (id && fullUrl) acc[id] = fullUrl;
      return acc;
    }, {});
  }, [imageList, excludedSet]);

  // Memoize filtered images to prevent recalculation
  const filteredImages = useMemo(() => {
    return imageList.filter((item) => {
      if (excludedSet.has(item.image_data?.upload_type)) return false;
      const id = String(item.image_data?.id ?? "");
      return !!validUrls[id];
    });
  }, [imageList, validUrls, excludedSet]);

  useEffect(() => {
    let isCancelled = false;

    const fetchImages = async () => {
      if (!session || isCancelled) return;
      const fetchKey = `${currentPatientDetail.uuid}|${excludedKey}|${session.accessToken ?? ""}`;
      if (lastFetchKeyRef.current === fetchKey) return;
      lastFetchKeyRef.current = fetchKey;
      setIsLoading(true);
      setError(null);

      try {
        const images = await getPatientImages(
          session,
          currentPatientDetail.uuid,
        );
        // console.log("Raw image data:", images);

        if (isCancelled) return;
        setImageList(images);

        // Process images in batches to avoid overwhelming the server
        const batchSize = 5;
        let urlMap: Record<string, string> = {};

        for (let i = 0; i < images.length; i += batchSize) {
          if (isCancelled) return;

          const batch = images.slice(i, i + batchSize);

          const batchResults = await Promise.allSettled(
            batch.map(async (item) => {
              if (excludedSet.has(item.image_data?.upload_type)) {
                return null;
              }
              const id = String(item.image_data?.id ?? "");
              if (!id) return null;

              const thumb = item.thumbnail_presigned_urls?.[0];
              const full = item.presigned_urls?.[0];
              let usable: string | null = null;

              // Try thumbnail first, then fallback to full image
              if (thumb && (await isImageLoadable(thumb))) {
                usable = thumb;
                console.log(`Using thumbnail for ${id}`);
              } else if (full && (await isImageLoadable(full))) {
                usable = full;
                console.log(`Falling back to full image for ${id}`);
              } else {
                console.log(`Both thumbnail and full image failed for ${id}`);
              }

              return usable ? { id, url: usable } : null;
            }),
          );

          // Process successful results
          batchResults.forEach((result) => {
            if (result.status === "fulfilled" && result.value) {
              const { id, url } = result.value;
              urlMap[id] = url;
            }
          });
        }

        // Update state once after all batches are processed
        if (!isCancelled) {
          setValidUrls(urlMap);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("Failed to load gallery images", err);
          setError("Unable to load photos. Try again.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchImages();

    return () => {
      isCancelled = true;
    };
  }, [
    session,
    currentPatientDetail.uuid,
    excludedKey,
    excludedSet,
    isImageLoadable,
  ]);

  // Reset state when patient changes
  useEffect(() => {
    setImageList([]);
    setValidUrls({});
    checkedUrlsRef.current = {};
    lastFetchKeyRef.current = null;
    setModalVisible(false);
    setSelectedIndex(0);
    setIsLoading(true);
    setError(null);
  }, [currentPatientDetail.uuid]);

  if (isLoading) {
    return (
      <View style={styles.stateContainer}>
        <ActivityIndicator size="small" color={tokens.color.focus} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.stateContainer}>
        <Text style={styles.stateText}>{error}</Text>
      </View>
    );
  }

  if (!filteredImages.length) {
    return (
      <View style={styles.stateContainer}>
        <Text style={styles.stateText}>No photos uploaded.</Text>
      </View>
    );
  }

  return (
    <View
      style={styles.scrollContainer}
      onLayout={(event) => {
        // Measure the actual container width if availableWidth not provided
        if (!availableWidth) {
          const { width } = event.nativeEvent.layout;
          setContainerWidth(width);
        }
      }}
    >
      <FlatList
        key={`gallery-${numColumns}`} // Add key that changes with numColumns
        data={filteredImages}
        keyExtractor={(item) => String(item.image_data?.id ?? Math.random())}
        numColumns={numColumns}
        contentContainerStyle={styles.gridContainer}
        renderItem={({ item, index }) => {
          const id = String(item.image_data?.id ?? "");
          const imageUrl = validUrls[id];
          if (!imageUrl) return null;
          const rawTags = item.image_data?.tags;
          const tagList = Array.isArray(rawTags)
            ? rawTags
            : rawTags
              ? [String(rawTags)]
              : [];
          const tagLabel = tagList.filter(Boolean).join(", ");

          return (
            <Pressable
              onPress={() => {
                setSelectedIndex(index);
                setModalVisible(true);
              }}
              style={styles.imageWrapper}
              // Add data attribute for debugging
              {...(typeof window !== "undefined" && {
                "data-image-id": id,
                "data-image-url": imageUrl,
              })}
            >
              <View style={styles.imageCard}>
                <Image
                  source={{ uri: imageUrl }}
                  style={styles.image}
                  resizeMode="cover"
                  onError={(error) => {
                    console.error(
                      `Image failed to load for ID ${id}:`,
                      error.nativeEvent.error,
                    );
                    console.error(`Failed URL: ${imageUrl}`);

                    // Remove from valid URLs so it doesn't keep trying to render
                    setValidUrls((prev) => {
                      const updated = { ...prev };
                      delete updated[id];
                      return updated;
                    });
                  }}
                  onLoad={() => {
                    // console.log(`Image loaded successfully for ID ${id}: ${imageUrl.substring(0, 50)}...`);
                  }}
                  onLoadStart={() => {
                    // console.log(`Started loading image for ID ${id}: ${imageUrl.substring(0, 50)}...`);
                  }}
                />
                {tagLabel ? (
                  <Text style={styles.tagText}>{tagLabel}</Text>
                ) : null}
              </View>
            </Pressable>
          );
        }}
      />

      <GalleryModal
        visible={modalVisible}
        imageUrls={filteredImages.map(
          (item) => fullImageUrlMap[String(item.image_data?.id ?? "")] ?? "",
        )}
        selectedIndex={selectedIndex}
        onClose={() => setModalVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  gridContainer: {
    padding: tokens.spacing.md,
    justifyContent: "center",
  },
  imageWrapper: {
    padding: tokens.spacing.xs,
  },
  imageCard: {
    gap: tokens.spacing.xs,
    alignItems: "flex-start",
  },
  image: {
    width: tokens.size.image,
    height: tokens.size.image,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.color.surface,
  },
  scrollContainer: {
    flex: 1,
    overflow: "hidden",
  },
  tagText: {
    color: tokens.color.muted,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  stateContainer: {
    minHeight: tokens.size.emptyMinHeight,
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing.md,
  },
  stateText: {
    color: tokens.color.muted,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
});

export default SimpleGalleryView;
